import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, InputAdornment, LinearProgress,
  MenuItem, Paper, Select, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { CiSearch } from "react-icons/ci";

import {
  useGetUsersListQuery,
  useCreateUserAccountMutation,
  useUpdateUserAccountMutation,
  useDeleteUserAccountMutation,
  useGenerateUserPasswordMutation,
  useSendUserCredentialsMutation,
  useResetUserCredentialsMutation,
  useSendUserLoginInfoMutation,
  useLazyGetUserLoginInfoPreviewQuery,
  useGetRolesQuery,
} from "../../../Api/attoDeskApi";
import { IRole, IUserListItem } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";

const AVATAR_COLORS = ["#2f3891", "#0ea5e9", "#16a34a", "#d97706", "#7c3aed", "#dc2626"];
const avatarColor = (name: string) => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
const initials = (name: string) =>
  name.split(" ").map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase();

type FormState = {
  first_name:                string;
  last_name:                 string;
  email:                     string;
  phone:                     string;
  password:                  string;
  role_id:                   string;
  send_email_notification:   boolean;
  send_payment_emails:       boolean;
};

const EMPTY_FORM: FormState = {
  first_name:               "",
  last_name:                "",
  email:                    "",
  phone:                    "",
  password:                 "",
  role_id:                  "",
  send_email_notification:  true,
  send_payment_emails:      false,
};

const formatPhone = (raw: string): string => {
  const digits = (raw ?? "").replace(/\D/g, "").slice(0, 15);
  if (digits.length === 0) return "";
  if (digits.length <= 3)  return `(${digits}`;
  if (digits.length <= 6)  return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)} ${digits.slice(10)}`;
};

type StrengthInfo = { label: string; score: number; color: string };
const passwordStrength = (pw: string): StrengthInfo => {
  if (!pw) return { label: "", score: 0, color: "#e2e8f0" };
  let score = 0;
  if (pw.length >= 6)  score += 1;
  if (pw.length >= 10) score += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw))   score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  if (score <= 2) return { label: "Weak",   score: 30,  color: "#ef4444" };
  if (score <= 3) return { label: "Fair",   score: 55,  color: "#f59e0b" };
  if (score <= 4) return { label: "Good",   score: 80,  color: "#2f3891" };
  return              { label: "Strong", score: 100, color: "#16a34a" };
};

const UsersPage: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  const { data, isFetching, refetch } = useGetUsersListQuery({ search: debouncedSearch });
  const { data: rolesData } = useGetRolesQuery();
  const [createUser, { isLoading: creating }] = useCreateUserAccountMutation();
  const [updateUser, { isLoading: updating }] = useUpdateUserAccountMutation();
  const [deleteUser, { isLoading: deleting }] = useDeleteUserAccountMutation();
  const [generatePw]                          = useGenerateUserPasswordMutation();
  const [sendCredentials, { isLoading: sending }] = useSendUserCredentialsMutation();
  const [resetCredentials, { isLoading: resetting }] = useResetUserCredentialsMutation();
  const [sendLoginInfo, { isLoading: sendingInfo }] = useSendUserLoginInfoMutation();
  const [fetchLoginInfoPreview] = useLazyGetUserLoginInfoPreviewQuery();

  const users: IUserListItem[] = useMemo(() => (data?.data as IUserListItem[]) ?? [], [data]);
  const roles: IRole[] = useMemo(() => (rolesData?.data as IRole[]) ?? [], [rolesData]);

  const adminCount = useMemo(
    () => users.filter((u) => (u.role_name ?? "").toLowerCase() === "admin").length,
    [users]
  );
  const isLastAdmin = (u: IUserListItem) =>
    (u.role_name ?? "").toLowerCase() === "admin" && adminCount <= 1;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<IUserListItem | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [showPw, setShowPw] = useState(false);
  const [pwVisible, setPwVisible] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IUserListItem | null>(null);

  const [successOpen, setSuccessOpen] = useState(false);
  const [successInfo, setSuccessInfo] = useState<{ id: number; email: string; password: string } | null>(null);

  const [resendTarget, setResendTarget] = useState<IUserListItem | null>(null);
  const [loginInfoBusy, setLoginInfoBusy] = useState<number | null>(null);

  const [loginInfoDialog, setLoginInfoDialog] = useState<{
    userId: number;
    userName: string;
    toEmail: string;
    subject: string;
    message: string;
  } | null>(null);

  const emailTaken = useMemo(() => {
    const e = form.email.trim().toLowerCase();
    if (!e) return false;
    return users.some(
      (u) => (u.email ?? "").toLowerCase() === e && u.id !== editTarget?.id
    );
  }, [form.email, users, editTarget]);

  const emailValid = /^\S+@\S+\.\S+$/.test(form.email.trim());
  const strength   = passwordStrength(form.password);

  const phoneDigits = form.phone.replace(/\D/g, "");
  // A phone is mandatory on both create and edit: every user is mirrored into a
  // `drivers` row whose phone column is NOT NULL. Allowing a blank here only
  // moved the failure to the database, where it surfaced as a SQL error.
  const phoneFilled = phoneDigits.length > 0;
  const phoneValid  = phoneDigits.length >= 10 && phoneDigits.length <= 15;

  const canSubmit =
    editTarget
      ? !!form.email.trim() && emailValid && !emailTaken && phoneValid
      : !!form.first_name.trim() &&
        !!form.last_name.trim()  &&
        emailValid && !emailTaken &&
        phoneValid &&
        !!form.role_id;

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setShowPw(false);
    setPwVisible(false);
    setDialogOpen(true);
  };

  const openEdit = (user: IUserListItem) => {
    setEditTarget(user);
    setForm({
      first_name: user.first_name ?? "",
      last_name:  user.last_name ?? "",
      email:      user.email ?? "",
      phone:      formatPhone(user.phone ?? ""),
      password:   "",
      role_id:    String(user.role_id ?? ""),
      send_email_notification: user.send_email_notification ?? true,
      send_payment_emails:     user.send_payment_emails ?? false,
    });
    setShowPw(false);
    setPwVisible(false);
    setDialogOpen(true);
  };

  const handleAutoGenerate = async () => {
    try {
      const res: any = await generatePw().unwrap();
      if (res?.password) {
        setForm((f) => ({ ...f, password: res.password }));
        setPwVisible(true);
      }
    } catch {
      const pool = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#$%&*!?";
      let p = "";
      for (let i = 0; i < 12; i++) p += pool[Math.floor(Math.random() * pool.length)];
      setForm((f) => ({ ...f, password: p }));
      setPwVisible(true);
    }
  };

  const handleSave = async () => {
    if (!canSubmit) return;
    try {
      if (editTarget) {
        const payload: any = {
          first_name: form.first_name.trim() || undefined,
          last_name:  form.last_name.trim()  || undefined,
          email:      form.email.trim(),
          phone:      phoneDigits || undefined,
          role_id:    form.role_id ? Number(form.role_id) : null,
          send_email_notification:  form.send_email_notification,
          send_payment_emails:      form.send_payment_emails,
        };
        if (showPw && form.password.trim()) payload.password = form.password.trim();
        await updateUser({ id: editTarget.id, ...payload }).unwrap();
        showMessage("User updated successfully", "success");
        setDialogOpen(false);
        refetch();
      } else {
        const payload = {
          first_name:               form.first_name.trim(),
          last_name:                form.last_name.trim(),
          email:                    form.email.trim(),
          phone:                    phoneDigits || undefined,
          role_id:                  Number(form.role_id),
          send_email_notification:  form.send_email_notification,
          send_payment_emails:      form.send_payment_emails,
          ...(form.password.trim() ? { password: form.password.trim() } : {}),
        };
        const res: any = await createUser(payload).unwrap();
        const created = res?.data;
        setDialogOpen(false);
        refetch();
        if (created?.id && created?.temp_password) {
          setSuccessInfo({ id: created.id, email: created.email, password: created.temp_password });
          setSuccessOpen(true);
        } else {
          showMessage("User created successfully", "success");
        }
      }
    } catch (err: any) {
      const msg =
        err?.data?.message ||
        (err?.data?.errors && Object.values(err.data.errors)[0] as any)?.[0] ||
        "Failed to save user";
      showErrorMessage(msg);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUser(deleteTarget.id).unwrap();
      showMessage("User deleted", "success");
      setDeleteDialogOpen(false);
      refetch();
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to delete user");
    }
  };

  const handleCopyPassword = async () => {
    if (!successInfo) return;
    try {
      await navigator.clipboard.writeText(successInfo.password);
      showMessage("Password copied to clipboard", "success");
    } catch {
      showErrorMessage("Unable to copy password");
    }
  };

  const handleSendCredentials = async () => {
    if (!successInfo) return;
    try {
      await sendCredentials({ id: successInfo.id, password: successInfo.password }).unwrap();
      showMessage("Credentials sent to " + successInfo.email, "success");
      setSuccessOpen(false);
      setSuccessInfo(null);
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to send credentials");
    }
  };

  const handleResendCredentials = async () => {
    if (!resendTarget) return;
    try {
      const res: any = await resetCredentials({ id: resendTarget.id, send_email: true }).unwrap();
      const created = res?.data;
      setResendTarget(null);
      if (created?.id && created?.temp_password) {
        setSuccessInfo({ id: created.id, email: created.email, password: created.temp_password });
        setSuccessOpen(true);
      }
      if (res?.mail_error) {
        showErrorMessage(res.mail_error);
      } else {
        showMessage(res?.message || "Password reset and credentials emailed", "success");
      }
      refetch();
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to reset credentials");
    }
  };

  const openLoginInfoDialog = async (u: IUserListItem) => {
    if (!u.email) {
      showErrorMessage("User has no email address.");
      return;
    }
    setLoginInfoBusy(u.id);
    try {
      const res: any = await fetchLoginInfoPreview(u.id).unwrap();
      const preview: any = res?.data ?? {};
      setLoginInfoDialog({
        userId: u.id,
        userName: u.full_name || u.username,
        toEmail: preview.to_email ?? u.email ?? "",
        subject: preview.subject ?? "",
        message: preview.message ?? "",
      });
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to load email preview");
    } finally {
      setLoginInfoBusy(null);
    }
  };

  const submitLoginInfo = async () => {
    if (!loginInfoDialog) return;
    if (!loginInfoDialog.toEmail.trim()) {
      showErrorMessage("Recipient email is required.");
      return;
    }
    try {
      await sendLoginInfo({
        id: loginInfoDialog.userId,
        to_email: loginInfoDialog.toEmail.trim(),
        subject: loginInfoDialog.subject,
        message: loginInfoDialog.message,
      }).unwrap();
      showMessage(`Login info sent to ${loginInfoDialog.toEmail.trim()}`, "success");
      setLoginInfoDialog(null);
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to send login info");
    }
  };

  const openProfile = (u: IUserListItem) => {
    navigate(`/user-management/users/${u.id}/profile`);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <GroupsOutlinedIcon sx={{ color: "#2f3891", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize={18} color="#0f172a">User Management</Typography>
            <Typography fontSize={12} color="#94a3b8">{users.length} user{users.length !== 1 ? "s" : ""} registered</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ textTransform: "none", borderRadius: 2, px: 2.5, bgcolor: "#2f3891", "&:hover": { bgcolor: "#252f78" } }}>
          Add User
        </Button>
      </Box>

      {/* Search */}
      <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, p: 2, mb: 3 }}>
        <TextField size="small" placeholder="Search users…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          name="user-search"
          autoComplete="off"
          inputProps={{ autoComplete: "off", "data-lpignore": "true", "data-1p-ignore": "true" }}
          InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch style={{ color: "#94a3b8" }} /></InputAdornment> }}
          sx={{ minWidth: 280 }} />
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, overflow: "hidden" }}>
        {isFetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#2f3891" }} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Email Notify</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u, idx) => (
                  <TableRow key={u.id} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar sx={{
                          width: 34, height: 34, fontSize: 13, fontWeight: 700,
                          bgcolor: avatarColor(u.full_name), color: "#fff",
                        }}>
                          {initials(u.full_name)}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={600} fontSize={13} color="#0f172a">{u.full_name}</Typography>
                          <Typography fontSize={11} color="#94a3b8">Login: {u.username}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={13} color="#64748b">{u.email ?? "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={13} color="#64748b">{u.phone ?? "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      {u.role_name ? (
                        <Chip label={u.role_name} size="small"
                          sx={{ bgcolor: "#eef2ff", color: "#2f3891", fontWeight: 600, fontSize: 11 }} />
                      ) : (
                        <Typography fontSize={12} color="#94a3b8">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
  <Chip
    label={u.send_email_notification ? "On" : "Off"}
    size="small"
    sx={{
      bgcolor: u.send_email_notification ? "#f0fdf4" : "#f8fafc",
      color: u.send_email_notification ? "#16a34a" : "#94a3b8",
      fontWeight: 600,
      fontSize: 11,
      border: "1px solid",
      borderColor: u.send_email_notification ? "#bbf7d0" : "#e2e8f0",
    }}
  />
</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                        <Tooltip title="View profile & activity">
                          <IconButton size="small" onClick={() => openProfile(u)}
                            sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#f1f5f9", color: "#475569", "&:hover": { bgcolor: "#e2e8f0" } }}>
                            <PersonOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={u.email ? "Email login info to user" : "Add an email first"}>
                          <span>
                            <IconButton size="small"
                              disabled={!u.email || loginInfoBusy === u.id}
                              onClick={() => openLoginInfoDialog(u)}
                              sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#f0fdf4", color: "#16a34a", "&:hover": { bgcolor: "#dcfce7" }, "&.Mui-disabled": { bgcolor: "#f1f5f9", color: "#cbd5e1" } }}>
                              {loginInfoBusy === u.id
                                ? <CircularProgress size={14} sx={{ color: "#16a34a" }} />
                                : <ForwardToInboxOutlinedIcon sx={{ fontSize: 14 }} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit user">
                          <IconButton size="small" onClick={() => openEdit(u)}
                            sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#eef2ff", color: "#2f3891", "&:hover": { bgcolor: "#e0e7ff" } }}>
                            <EditSharpIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isLastAdmin(u) ? "At least one Admin must exist" : "Delete user"}>
                          <span>
                            <IconButton size="small"
                              disabled={isLastAdmin(u)}
                              onClick={() => { setDeleteTarget(u); setDeleteDialogOpen(true); }}
                              sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#fff5f5", color: "#ef4444", "&:hover": { bgcolor: "#fee2e2" }, "&.Mui-disabled": { bgcolor: "#f1f5f9", color: "#cbd5e1" } }}>
                              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: "#94a3b8" }}>
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ height: 4, bgcolor: "#2f3891" }} />
        <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography fontWeight={700} fontSize={15}>{editTarget ? "Edit User" : "Create New User"}</Typography>
          <IconButton size="small" onClick={() => setDialogOpen(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          {/* Decoy fields to absorb browser autofill */}
          <input type="text" name="prevent_autofill" autoComplete="off" style={{ display: "none" }} />
          <input type="password" name="prevent_autofill_pw" autoComplete="new-password" style={{ display: "none" }} />

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField label="First Name *" size="small" fullWidth value={form.first_name}
              name="new-user-firstname"
              autoComplete="off"
              inputProps={{ autoComplete: "off", "data-lpignore": "true", "data-1p-ignore": "true" }}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            <TextField label="Last Name *" size="small" fullWidth value={form.last_name}
              name="new-user-lastname"
              autoComplete="off"
              inputProps={{ autoComplete: "off", "data-lpignore": "true", "data-1p-ignore": "true" }}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </Box>

          <Box>
            <TextField label="Email Address *" size="small" fullWidth type="email" value={form.email}
              name="new-user-email"
              autoComplete="off"
              error={(form.email.length > 0 && !emailValid) || emailTaken}
              helperText={
                emailTaken
                  ? "This email is already taken by another user."
                  : form.email.length > 0 && !emailValid
                    ? "Enter a valid email address."
                    : "This email will be used as the login username"
              }
              InputProps={{ startAdornment: <InputAdornment position="start"><MailOutlineIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment> }}
              inputProps={{ autoComplete: "off", "data-lpignore": "true", "data-1p-ignore": "true" }}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Box>

          <TextField label="Phone Number" size="small" fullWidth value={form.phone}
            name="new-user-phone"
            autoComplete="off"
            required
            error={form.phone.length > 0 && !phoneValid}
            helperText={
              form.phone.length > 0 && !phoneValid
                ? "Enter a valid phone number"
                : !phoneFilled
                ? "Required"
                : ""
            }
            InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlinedIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment> }}
            inputProps={{ autoComplete: "off", inputMode: "tel", "data-lpignore": "true", "data-1p-ignore": "true" }}
            onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
            onBlur={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })} />

          {/* Password section */}
          {!editTarget ? (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography fontSize={12} fontWeight={600} color="#475569">
                  Password <Typography component="span" fontSize={11} color="#94a3b8">(leave blank to auto-generate)</Typography>
                </Typography>
                <Tooltip title="Auto-generate a strong password">
                  <IconButton size="small" onClick={handleAutoGenerate}
                    sx={{ color: "#2f3891" }}>
                    <AutoFixHighOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField size="small" fullWidth value={form.password}
                type={pwVisible ? "text" : "password"}
                name="new-user-password"
                autoComplete="new-password"
                inputProps={{ autoComplete: "new-password", "data-lpignore": "true", "data-1p-ignore": "true" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setPwVisible((v) => !v)}>
                        {pwVisible
                          ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                          : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
              {form.password.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography fontSize={11} fontWeight={600} sx={{ color: strength.color }}>
                      {strength.label}
                    </Typography>
                    <Typography fontSize={11} color="#94a3b8">
                      {form.password.length} chars
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={strength.score}
                    sx={{
                      height: 6, borderRadius: 3, bgcolor: "#eef2f7",
                      "& .MuiLinearProgress-bar": { bgcolor: strength.color, borderRadius: 3 },
                    }} />
                </Box>
              )}
            </Box>
          ) : (
            <>
              {!showPw ? (
                <Button size="small" variant="outlined" onClick={() => setShowPw(true)}
                  sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b" }}>
                  Change Password
                </Button>
              ) : (
                <Box>
                  <TextField label="New Password" size="small" fullWidth type="password" value={form.password}
                    name="new-user-password"
                    autoComplete="new-password"
                    inputProps={{ autoComplete: "new-password", "data-lpignore": "true", "data-1p-ignore": "true" }}
                    onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  <Button size="small" onClick={() => { setShowPw(false); setForm({ ...form, password: "" }); }}
                    sx={{ mt: 0.5, fontSize: 11, textTransform: "none", color: "#94a3b8", p: 0 }}>
                    Cancel password change
                  </Button>
                </Box>
              )}
            </>
          )}

          <Box>
            <Typography fontSize={12} fontWeight={600} color="#475569" mb={0.5}>Role *</Typography>
            <Select size="small" fullWidth displayEmpty value={form.role_id}
              onChange={(e) => setForm({ ...form, role_id: e.target.value as string })}>
              <MenuItem value=""><em style={{ color: "#94a3b8", fontStyle: "normal" }}>Select a role</em></MenuItem>
              {roles.map((r) => <MenuItem key={r.id} value={String(r.id)}>{r.name}</MenuItem>)}
            </Select>
          </Box>

        
        
            <Paper
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: form.send_email_notification ? "#c7d2fe" : "#e2e8f0",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                bgcolor: form.send_email_notification ? "#eef2ff" : "#f8fafc",
                transition: "all 0.2s ease",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <NotificationsOutlinedIcon
                    sx={{
                      fontSize: 18,
                      color: form.send_email_notification ? "#2f3891" : "#94a3b8",
                      transition: "color 0.2s ease",
                    }}
                  />
                  <Box>
                    <Typography fontSize={13} fontWeight={600} color={form.send_email_notification ? "#2f3891" : "#475569"}>
                      Send Email Notification
                    </Typography>
                    {/* <Typography fontSize={11} color="#94a3b8" mt={0.2}>
                      {form.send_email_notification
                        ? "Welcome email with login details will be sent"
                        : "No email will be sent to the user"}
                    </Typography> */}
                  </Box>
                </Box>
                <Switch
                  checked={form.send_email_notification}
                  onChange={(e) => setForm({ ...form, send_email_notification: e.target.checked })}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#2f3891" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#2f3891" },
                  }}
                />
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: form.send_payment_emails ? "#c7d2fe" : "#e2e8f0",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                bgcolor: form.send_payment_emails ? "#eef2ff" : "#f8fafc",
                transition: "all 0.2s ease",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <PaymentsOutlinedIcon
                    sx={{
                      fontSize: 18,
                      color: form.send_payment_emails ? "#2f3891" : "#94a3b8",
                      transition: "color 0.2s ease",
                    }}
                  />
                  <Box>
                    <Typography fontSize={13} fontWeight={600} color={form.send_payment_emails ? "#2f3891" : "#475569"}>
                      Send Payment Emails
                    </Typography>
                    <Typography fontSize={11} color="#94a3b8" mt={0.2}>
                      {form.send_payment_emails
                        ? "Notified whenever an online payment is received"
                        : "No online payment notifications"}
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={form.send_payment_emails}
                  onChange={(e) => setForm({ ...form, send_payment_emails: e.target.checked })}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#2f3891" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#2f3891" },
                  }}
                />
              </Box>
            </Paper>

        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setDialogOpen(false)}
            sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}
            disabled={creating || updating || !canSubmit}
            sx={{ textTransform: "none", borderRadius: 1.5, bgcolor: "#2f3891", "&:hover": { bgcolor: "#252f78" }, minWidth: 80 }}>
            {(creating || updating) ? <CircularProgress size={18} color="inherit" /> : editTarget ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post-create success dialog */}
      <Dialog open={successOpen} onClose={() => { setSuccessOpen(false); setSuccessInfo(null); }}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ height: 4, bgcolor: "#2f3891" }} />
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15 }}>User Created Successfully</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper elevation={0}
            sx={{ bgcolor: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 2, p: 2, display: "flex", gap: 1.5 }}>
            <CheckCircleOutlineIcon sx={{ color: "#059669", fontSize: 22, mt: 0.2 }} />
            <Box>
              <Typography fontWeight={700} fontSize={13} color="#065f46">Account ready</Typography>
              <Typography fontSize={13} color="#065f46" mt={0.3}>
                <strong>{successInfo?.email}</strong> can now log in.
                Would you like to send their credentials via email?
              </Typography>
            </Box>
          </Paper>

          <Box>
            <Typography fontSize={11} fontWeight={700} color="#64748b" letterSpacing={0.5}>LOGIN EMAIL</Typography>
            <Typography fontSize={14} color="#0f172a" fontWeight={600} mt={0.5}>{successInfo?.email}</Typography>
          </Box>

          <Box>
            <Typography fontSize={11} fontWeight={700} color="#64748b" letterSpacing={0.5} mb={0.5}>GENERATED PASSWORD</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField size="small" fullWidth value={successInfo?.password ?? ""}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: "monospace", fontWeight: 600, letterSpacing: 0.5 },
                }} />
              <Tooltip title="Copy password">
                <IconButton onClick={handleCopyPassword}
                  sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, color: "#2f3891" }}>
                  <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography fontSize={11} color="#94a3b8" mt={0.75}>
              Save this password — it will not be shown again.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => { setSuccessOpen(false); setSuccessInfo(null); }}
            sx={{ textTransform: "none", color: "#64748b" }}>
            Skip
          </Button>
          <Button variant="contained" onClick={handleSendCredentials} disabled={sending}
            startIcon={!sending ? <MailOutlineIcon sx={{ fontSize: 18 }} /> : undefined}
            sx={{ textTransform: "none", borderRadius: 1.5, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" }, minWidth: 160 }}>
            {sending ? <CircularProgress size={18} color="inherit" /> : "Send via Email"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete User?</DialogTitle>
        <DialogContent>
          <Typography fontSize={14} color="text.secondary">
            Are you sure you want to delete <strong>{deleteTarget?.full_name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            sx={{ textTransform: "none" }}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send login info — editable preview */}
      <Dialog open={!!loginInfoDialog} onClose={() => setLoginInfoDialog(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ height: 4, bgcolor: "#16a34a" }} />
        <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ForwardToInboxOutlinedIcon sx={{ color: "#16a34a" }} />
            <Typography fontWeight={700} fontSize={15}>Send Login Info</Typography>
          </Box>
          <IconButton size="small" onClick={() => setLoginInfoDialog(null)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <Typography fontSize={12} color="#64748b">
            Review and edit the message before sending. No password will be included.
          </Typography>

          <TextField label="To" size="small" fullWidth type="email"
            value={loginInfoDialog?.toEmail ?? ""}
            onChange={(e) => setLoginInfoDialog((d) => d ? { ...d, toEmail: e.target.value } : d)}
            InputProps={{ startAdornment: <InputAdornment position="start"><MailOutlineIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment> }}
            helperText={loginInfoDialog ? `Sending to ${loginInfoDialog.userName}` : ""} />

          <TextField label="Subject" size="small" fullWidth
            value={loginInfoDialog?.subject ?? ""}
            onChange={(e) => setLoginInfoDialog((d) => d ? { ...d, subject: e.target.value } : d)} />

          <TextField label="Message" size="small" fullWidth multiline minRows={9} maxRows={18}
            value={loginInfoDialog?.message ?? ""}
            onChange={(e) => setLoginInfoDialog((d) => d ? { ...d, message: e.target.value } : d)}
            helperText="Line breaks are preserved in the email." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setLoginInfoDialog(null)}
            sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitLoginInfo} disabled={sendingInfo}
            startIcon={!sendingInfo ? <ForwardToInboxOutlinedIcon sx={{ fontSize: 18 }} /> : undefined}
            sx={{ textTransform: "none", borderRadius: 1.5, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" }, minWidth: 120 }}>
            {sendingInfo ? <CircularProgress size={18} color="inherit" /> : "Send Email"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset password confirmation */}
      <Dialog open={!!resendTarget} onClose={() => setResendTarget(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15 }}>Reset password?</DialogTitle>
        <DialogContent>
          <Typography fontSize={13} color="text.secondary">
            A new password will be generated for{" "}
            <strong>{resendTarget?.full_name}</strong> and emailed to{" "}
            <strong>{resendTarget?.email}</strong>. Their current password will stop working immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResendTarget(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" onClick={handleResendCredentials} disabled={resetting}
            startIcon={!resetting ? <AutoFixHighOutlinedIcon sx={{ fontSize: 18 }} /> : undefined}
            sx={{ textTransform: "none", borderRadius: 1.5, bgcolor: "#c2410c", "&:hover": { bgcolor: "#9a3412" } }}>
            {resetting ? <CircularProgress size={18} color="inherit" /> : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default UsersPage;